import { DatePicker, Input, Modal, Popconfirm, Tooltip } from 'antd';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Button } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined } from '@ant-design/icons';

import { useAuth } from '../api/AuthContext.js';
import { deleteEvent } from '../api/deleteEvent.js';
import { runCron } from '../api/runCron.js';
import { updateSession } from '../api/updateSession.js';
import { showToast, updateToast } from '../utils/toastUtils.js';
import * as utils from '../utils/utils.js';
import Venue from './Venue.js';
import * as testUtils from '../utils/testUtils.js';
import { useTranslation } from 'react-i18next';
import { pauseEvent } from '../api/pauseEvent.js';
import { resumeEvent } from '../api/resumeEvent.js';

export default function EventItemAdmin({ event, refreshEvents }) {
    const auth = useAuth();
    const { t, i18n } = useTranslation();
    const isPaused = !!event.is_paused;

    // Note: It is possible for an Event to have no Session, if the Session is
    // in the past and the cron job has not created a new one. In those cases we
    // use null or fall back to defaults from the event, when available. The
    // only exception is the sessionId, because it is used as an identifier on
    // the page for tests to pick up, and we want it to be something human
    // searchable (for easier debugging if tests fail).
    const session = testUtils.simulatingNullSession(event.event_id)
        ? null
        : event.sessions[0];
    const sessionId = session ? session.session_id : 'missing-session';
    const sessionActive = session ? session.active : false;
    const sessionVenueId = session ? session.venue_id : event.venue_id;
    const sessionVenueName = session ? session.venue.name : event.venue.name;
    const sessionAdminAlert = session ? session.admin_alert : null;
    const sessionTimestamp = session ? session.timestamp : null;

    const navigate = useNavigate();
    const [isShowingDeleteConfirmation, setIsShowingDeleteConfirmation] =
        useState(false);
    const [event_date, setEventDate] = useState(null);
    const [venue_id, setNextSessionVenueId] = useState(sessionVenueId);
    const [adminAlert, setAdminAlert] = useState(sessionAdminAlert);
    const [active, setActive] = useState(true);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

    const handleEditEvent = (event_id) => {
        navigate('/admin/events/' + event_id);
    };

    const handleSessionActiveChange = (e) => {
        setActive(e.target.checked);
    };

    const handleUpdateSession = async (e) => {
        if (event_date < dayjs()) {
            showToast(
                'Events cannot occur in the past.',
                auth.currentUser.email,
                {
                    toastId: 'toastIdErrorUpdatingSession',
                    type: 'error',
                    autoClose: 4000,
                    isLoading: false,
                }
            );
            return;
        }

        const toastId = showToast('One moment...', auth.currentUser.email, {
            toastId: 'toastIdSaveSession',
        });
        try {
            const timestamp = event_date ? event_date.toISOString() : null;

            await updateSession(
                sessionId,
                timestamp,
                venue_id,
                adminAlert,
                active
            );
            updateToast(toastId, auth.currentUser.email, {
                render: 'Session saved',
                type: 'success',
                isLoading: false,
                autoClose: 1500,
            });
            refreshEvents();
            setOpen(false);
        } catch (err) {
            console.error(err);
            updateToast(toastId, auth.currentUser.email, {
                render: 'Failed to save session',
                type: 'error',
                isLoading: false,
                autoClose: 1500,
            });
        }
    };

    const commitDeleteEvent = async (event_id) => {
        const toastId = showToast('One moment...', auth.currentUser.email, {
            toastId: 'toastIdDeleteEvent',
        });

        try {
            await deleteEvent(event_id);
            updateToast(toastId, auth.currentUser.email, {
                render: 'Event deleted',
                type: 'success',
                isLoading: false,
                autoClose: 1500,
            });
            refreshEvents();
            setIsShowingDeleteConfirmation(false);
        } catch (err) {
            console.error(err);
        }
    };

    const startCron = async () => {
        const response = await runCron();
        if (response && response.status === 200) {
            // Should we maybe refresh the page instead?
            showToast('Session creation started...', auth.currentUser.email, {
                toastId: 'toastIdCronStarted',
                type: 'success',
                autoClose: 4000,
                isLoading: false,
            });
        } else {
            showToast('Failed create session.', auth.currentUser.email, {
                toastId: 'toastIdCronFailure',
                type: 'error',
                autoClose: 4000,
                isLoading: false,
            });
        }
    };

    // Pause handler
    const handlePauseEvent = async () => {
        const toastId = showToast(
            t('Pausing event...'),
            auth.currentUser.email,
            { toastId: 'toastPause' }
        );
        try {
            await pauseEvent(event.event_id);
            updateToast(toastId, auth.currentUser.email, {
                render: t('Event paused'),
                type: 'success',
                isLoading: false,
                autoClose: 1500,
            });
            refreshEvents();
        } catch (err) {
            console.error(err);
            updateToast(toastId, auth.currentUser.email, {
                render: t('Failed to pause event'),
                type: 'error',
                isLoading: false,
                autoClose: 1500,
            });
        }
    };

    // Resume handler
    const handleResumeEvent = async () => {
        const toastId = showToast(
            t('Resuming event...'),
            auth.currentUser.email,
            { toastId: 'toastResume' }
        );
        try {
            await resumeEvent(event.event_id);
            updateToast(toastId, auth.currentUser.email, {
                render: t('Event resumed'),
                type: 'success',
                isLoading: false,
                autoClose: 1500,
            });
            refreshEvents();
        } catch (err) {
            console.error(err);
            updateToast(toastId, auth.currentUser.email, {
                render: t('Failed to resume event'),
                type: 'error',
                isLoading: false,
                autoClose: 1500,
            });
        }
    };

    // Controls whether to show the 'Session unavailable' message and Edit
    // Session button.
    const sessionUnavailable =
        !session || (!sessionTimestamp && event.type !== utils.SINGLE_EVENT);

    const tagColor = event.type === utils.SINGLE_EVENT ? 'orange' : 'green';
    const eventTypeTag = (
        <span className={`tag ${tagColor}`} style={{ marginLeft: '8px', fontSize: '11px' }}>
            {event.type === utils.SINGLE_EVENT
                ? 'Single Event'
                : utils.dayNumberToDayName(event.weekday || 0) + 's'}
        </span>
    );

    const pausedBadge = isPaused && (
        <span style={{
            marginLeft: '8px',
            padding: '2px 8px',
            background: '#fff3cd',
            color: '#856404',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '600',
            border: '1px solid #ffeaa7'
        }}>
            ⏸ Paused
        </span>
    );

    let nameLink = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <a
                // eslint-disable-next-line react/no-unknown-property
                session={sessionId}
                id={'link-event-' + event.event_id}
                onClick={() => handleEditEvent(event.event_id)}
                style={{ fontWeight: '600', fontSize: '14px', color: '#1f2937', cursor: 'pointer', textDecoration: 'none' }}
            >
                {event.name}
            </a>
            {eventTypeTag}
            {pausedBadge}
            {event.short_description && (
                <Tooltip title={event.short_description}>
                    <img
                        src="/img/shout.svg"
                        alt="Short description"
                        style={{ height: 18, cursor: 'pointer' }}
                    />
                </Tooltip>
            )}
        </div>
    );

    let eventInfo = (
        <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {event.type === utils.SINGLE_EVENT
                ? 'One-time event'
                : `Repeats every ${utils.dayNumberToDayName(event.weekday || 0)} at ${dayjs(event.time_of_day, 'HH:mm:ss', true).format('HH:mm')}`}
        </div>
    );

    // Modal specific
    const [open, setOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    const showModal = () => {
        if (sessionUnavailable) return;
        setOpen(true);
        if (sessionTimestamp) {
            setEventDate(dayjs(sessionTimestamp));
        }
    };
    const handleOk = () => {
        handleUpdateSession();
        setConfirmLoading(true);
    };
    const handleCancel = () => {
        setOpen(false);
    };

    const handleVenueIdChange = (newVenueId) => {
        setNextSessionVenueId(newVenueId);
    };

    const handleAdminAlertChange = (e) => {
        setAdminAlert(e.target.value);
    };

    const activeCheckmark = sessionActive && (
        <img
            src="/img/check.svg"
            alt={'Session active'}
            style={{ paddingTop: 4 }}
            title={'This session is active'}
            onClick={showModal}
        />
    );
    const disabledCheckmark = !sessionActive && (
        <img
            src="/img/nocheck.svg"
            alt={'Session unavailable'}
            style={{ paddingTop: 4 }}
            title={
                sessionUnavailable
                    ? 'This session is unavailable'
                    : 'This session has been disabled'
            }
            onClick={showModal}
        />
    );

    return (
        <tr
            className={event.type === utils.SINGLE_EVENT ? 'single' : ''}
            style={{
                borderBottom: '1px solid #e8e8e8',
                transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
        >
            {/* Event Name + Type + Status */}
            <td style={{ padding: '8px 12px', verticalAlign: 'middle', minWidth: '200px' }}>
                {nameLink}
                {eventInfo}
            </td>

            {/* Session Status Icon */}
            <td style={{ padding: '8px 12px', textAlign: 'center', verticalAlign: 'middle', width: '80px' }}>
                {sessionActive ? (
                    <Tooltip title="Session is active">
                        <img
                            src="/img/check.svg"
                            alt="Session active"
                            style={{ width: 20, cursor: 'pointer' }}
                        />
                    </Tooltip>
                ) : (
                    <Tooltip title={sessionUnavailable ? 'Session unavailable' : 'Session disabled'}>
                        <img
                            src="/img/nocheck.svg"
                            alt="Session inactive"
                            style={{ width: 20, cursor: 'pointer' }}
                        />
                    </Tooltip>
                )}
            </td>

            {/* Next Session Info */}
            <td style={{ padding: '8px 12px', verticalAlign: 'middle', minWidth: '180px' }}>
                {sessionTimestamp ? (
                    <div>
                        <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>
                            {dayjs(sessionTimestamp).format('Do MMM YYYY, HH:mm')}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            📍 {sessionVenueName}
                            {sessionAdminAlert && (
                                <Tooltip title={sessionAdminAlert}>
                                    <img
                                        src="/img/shout.svg"
                                        alt="Admin alert"
                                        style={{ height: 16 }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                    </div>
                ) : (
                    <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        <span>No session available</span>
                    </div>
                )}
            </td>

            {/* Actions Column */}
            <td style={{ padding: '8px 12px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {/* Edit Session Button */}
                    {!sessionUnavailable && (
                        <Tooltip title="Edit next session details">
                            <Button
                                id={'editSession-' + sessionId}
                                size="small"
                                onClick={showModal}
                                style={{ fontSize: '12px' }}
                            >
                                Edit Session
                            </Button>
                        </Tooltip>
                    )}

                    {/* Create Session Button */}
                    {sessionUnavailable && (
                        <Tooltip title="Create next session">
                            <Button
                                size="small"
                                onClick={startCron}
                                type="primary"
                                style={{ fontSize: '12px' }}
                            >
                                Create Session
                            </Button>
                        </Tooltip>
                    )}

                    {/* Pause/Resume Button - Only for repeating events */}
                    {event.type !== utils.SINGLE_EVENT && (
                        <>
                            {isPaused ? (
                                <Tooltip title="Resume event - new sessions will be created on schedule">
                                    <Button
                                        icon={<PlayCircleOutlined />}
                                        onClick={handleResumeEvent}
                                        size="small"
                                        type="default"
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip title="Pause event - no new sessions will be created">
                                    <Button
                                        icon={<PauseCircleOutlined />}
                                        onClick={handlePauseEvent}
                                        size="small"
                                        type="default"
                                    />
                                </Tooltip>
                            )}
                        </>
                    )}
                </div>
            </td>

            {/* View Link */}
            <td style={{ padding: '8px 12px', textAlign: 'center', verticalAlign: 'middle', width: '80px' }}>
                <Link
                    to={window.location.origin + '/session/event/' + event.event_id}
                    style={{ whiteSpace: 'nowrap', fontSize: '12px' }}
                >
                    <Tooltip title="View session page">
                        <img
                            src="/img/view.svg"
                            alt="View session"
                            style={{
                                width: 20,
                                cursor: 'pointer',
                            }}
                        />
                    </Tooltip>
                </Link>
            </td>

            {isShowingDeleteConfirmation && (
                <div className="modal is-active">
                    <div className="modal-background"></div>
                    <div className="modal-card">
                        <header className="modal-card-head">
                            <p className="modal-card-title">
                                Delete event: {event.name}?
                            </p>
                            <button
                                className="delete"
                                aria-label="close"
                                onClick={() =>
                                    setIsShowingDeleteConfirmation(false)
                                }
                            ></button>
                        </header>
                        <section className="modal-card-body">
                            Are you sure? This can not be undone!
                        </section>
                        <footer className="modal-card-foot">
                            <button
                                id="confirmDeleteEvent"
                                className="button is-danger"
                                onClick={() =>
                                    commitDeleteEvent(event.event_id)
                                }
                            >
                                Confirm delete
                            </button>
                            <button
                                id="cancelDeleteEvent"
                                className="button"
                                onClick={() =>
                                    setIsShowingDeleteConfirmation(
                                        !isShowingDeleteConfirmation
                                    )
                                }
                            >
                                Cancel
                            </button>
                        </footer>
                    </div>
                </div>
            )}
            <Modal
                title={'Change next session only for: ' + event.name}
                open={open}
                onOk={handleUpdateSession}
                confirmLoading={confirmLoading}
                onCancel={handleCancel}
                okButtonProps={{ disabled: isDatePickerOpen }} // Disable OK button when DatePicker is open
            >
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                    }}
                >
                    <h4>
                        <strong>Time</strong>
                    </h4>
                    <DatePicker
                        id="singleSessionDayTime"
                        popupClassName="singleSessionTimeClass"
                        showTime
                        format={'YYYY/MM/DD HH:mm'}
                        size={'large'}
                        disabledDate={utils.disableDateYesterday}
                        onChange={(value) => {
                            setEventDate(value);
                            setIsDatePickerOpen(false);
                        }}
                        value={event_date}
                        needConfirm={utils.isTestUser(auth.currentUser.email)}
                        onOpenChange={setIsDatePickerOpen}
                    />
                    <input
                        id={'eventActive'}
                        type="checkbox"
                        name="activation"
                        defaultChecked={sessionActive}
                        onChange={handleSessionActiveChange}
                        className="switch is-rounded is-success"
                    />
                    <label key={'label'} htmlFor={'eventActive'}>
                        <span>Active</span>
                    </label>

                    <h4>
                        <Venue
                            venue_id={venue_id}
                            setVenueId={handleVenueIdChange}
                        />
                    </h4>

                    <h4>
                        <strong>Admin alert</strong> -{' '}
                        <small>Appears at the top of the session</small>
                    </h4>
                    <Input
                        id="editAdminAlert"
                        value={adminAlert}
                        onChange={handleAdminAlertChange}
                    />
                </div>
            </Modal>
        </tr>
    );
}
